import { createDecryptStream, deriveFileKey } from "@limeshare/lsc1";
import { downloadZip, predictLength } from "client-zip";
import type { OpenedFile, OpenedTransfer } from "./download";
import type { Sink } from "./sinks";
import { safeFileName } from "../util";

export interface ZipProgress {
  fileIndex: number;
  fileName: string;
  filesTotal: number;
  bytesDone: number;
  bytesTotal: number;
}

/**
 * Predicts the exact size of the final uncompressed ZIP archive.
 */
export function estimateZipSize(files: OpenedFile[]): number {
  const descriptors = files.map((f) => ({
    name: safeFileName(f.meta.name),
    size: f.meta.size,
  }));
  return Number(predictLength(descriptors));
}

/**
 * Streams multiple decrypted files into a single ZIP64 archive directly to the chosen sink.
 * Memory: strictly O(1) buffer — files are fetched and decrypted sequentially on the fly.
 */
export async function downloadAllAsZip(
  t: OpenedTransfer,
  sink: Sink,
  onProgress: (p: ZipProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  const filesTotal = t.files.length;
  const bytesTotal = t.files.reduce((acc, f) => acc + f.meta.size, 0);
  let bytesDone = 0;

  async function* fileGenerator() {
    for (let i = 0; i < t.files.length; i++) {
      if (signal?.aborted) throw new Error("ZIP download cancelled");
      const file = t.files[i]!;
      const cleanName = safeFileName(file.meta.name);

      onProgress({
        fileIndex: i,
        fileName: cleanName,
        filesTotal,
        bytesDone,
        bytesTotal,
      });

      const fileKey = await deriveFileKey(t.masterKey, t.transferId, file.fileId);
      const res = await fetch(file.downloadUrl, { signal: signal ?? null, mode: "cors" });
      if (!res.ok || !res.body) {
        throw new Error(res.status === 403 ? "Download link expired — reload the page." : `Storage error ${res.status}`);
      }

      let fileBytesRead = 0;
      const decrypt = createDecryptStream({
        key: fileKey,
        expectedEncryptedSize: file.encryptedSize,
        onProgress: (done) => {
          const delta = done - fileBytesRead;
          fileBytesRead = done;
          bytesDone += delta;
          onProgress({
            fileIndex: i,
            fileName: cleanName,
            filesTotal,
            bytesDone,
            bytesTotal,
          });
        },
      });

      const decryptedStream = res.body.pipeThrough(decrypt);

      yield {
        name: cleanName,
        input: decryptedStream,
        size: file.meta.size,
        lastModified: file.meta.mtime ? new Date(file.meta.mtime) : new Date(),
      };
    }
  }

  const zipResponse = downloadZip(fileGenerator());
  if (!zipResponse.body) throw new Error("Could not initialize ZIP stream");

  try {
    await sink.write(zipResponse.body);
  } catch (e) {
    await sink.abort();
    throw e;
  }
}
